import React, { useContext, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';

import { TimeRangeContext } from "../../../context/TimeRangeContext";
import Helper from '../../../helpers/Helper';

/**
 * Renders a host peak statistic alongside an optional previous-period comparison.
 * The previous peak is passed in by the parent (host list / detail) which fetches all
 * per-host stats in a single v2 batch call. This component no longer issues its own request.
 */
function PeakStatistic(props: {
    hostId: number,
    statistic: string,
    metric: number | null,
    previousMetric?: number | null,
}) {
    const timeRangeContext = useContext(TimeRangeContext);

    const comparisonSupported = useMemo(
        () => Helper.isComparisonSupported(timeRangeContext),
        [timeRangeContext]
    );

    const previousMetric = props.previousMetric ?? null;
    const showComparison =
        comparisonSupported &&
        previousMetric !== null &&
        previousMetric !== Infinity &&
        previousMetric !== -Infinity;

    const differenceMetric = useMemo(() => {
        if (!showComparison) return null;
        return Helper.getPercentageDifference(props.metric, previousMetric);
    }, [showComparison, props.metric, previousMetric]);

    if (props.metric === null) {
        return <Skeleton/>;
    }

    return (
        <React.Fragment>
            <span className="table-metric-value">
                {Math.round(props.metric)}%
            </span>
            {showComparison && (
                <span className="comparison">
                    {previousMetric}%
                    {differenceMetric !== null && (
                        <span>({differenceMetric}%)</span>
                    )}
                    {props.metric === previousMetric ? (
                        <i className="fal fa-equals fa-fw equals"/>
                    ) : (
                        (props.metric ?? 0) > (previousMetric ?? 0) ? (
                            <i className="fal fa-long-arrow-up fa-fw increase"/>
                        ) : (
                            <i className="fal fa-long-arrow-up fa-fw decrease"/>
                        )
                    )}
                </span>
            )}
        </React.Fragment>
    );
}

export default PeakStatistic;
